import { map } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  checksPhytoCategoryColor,
  checksTypeColor,
} from '../../../assets/data/enum/colorConfiguration';
import {
  ChecksResult,
  ChecksResultByCommodity,
  ChecksResultByDate,
} from '../_models/checks/ChecksResult';
import { CommonService } from './common.service';
import { CommonChemicalElement } from '../_models/commonService';

@Injectable()
export class ChecksService {
  private apiBaseUrl = environment.apiBaseUrl;
  private checksServiceUrl = environment.checksServiceUrl;
  $commonChecksElements: BehaviorSubject<CommonChemicalElement[]> = new BehaviorSubject<
    CommonChemicalElement[]
  >(null);
  $faChecksResults: BehaviorSubject<ChecksResult[]> = new BehaviorSubject<ChecksResult[]>(null);
  constructor(
    private http: HttpClient,
    private translate: TranslateService,
    private commonService: CommonService
  ) {}

  /**
   * Fetches and stores elements belonging to the 'CHECKS' group.
   *
   * This method retrieves common elements categorized under 'CHECKS' from `commonService`,
   * updates the `$commonChecksElements` Behaviour Subject, and returns the fetched elements.
   *
   * @returns {Promise<CommonChemicalElement[]>} A promise resolving to an array of elements.
   * @throws {Error} If the fetch operation fails.
   */
  async getAndStoreChecksElements(): Promise<CommonChemicalElement[]> {
    try {
      const elements = await this.commonService.getCommonElementsByGroupName('CHECKS').toPromise();
      this.$commonChecksElements.next(elements);
      return elements;
    } catch (e) {
      throw new Error(e);
    }
  }

  getFACheckResults(year): Observable<ChecksResult[]> {
    // we call the checks results on the fieldActivities applying the filter for the crop year and
    // the filter for the 'state' of result: checkResult=not_ok&checkResult=ok - we exclude the results in 'nd' (undefined)
    return this.http
      .get<any>(
        `${this.checksServiceUrl}api/private/v1/fa-check-result/?cropYear=${year}&checkResult=not_ok&checkResult=ok`
      )
      .pipe(
        map((response) => {
          this.$faChecksResults.next(response);
          return response;
        })
      );
  }

  clearFACheckResults(): void {
    this.$faChecksResults.next(null);
  }

  getFieldActivityCheckResultsByCommodity(
    startDate,
    endDate,
    year
  ): Observable<ChecksResultByCommodity[]> {
    return this.http
      .get<any>(
        `${this.checksServiceUrl}api/private/v1/fa-check-result/commodity/?cropYear=${year}&startDate=${startDate}&endDate=${endDate}`
      )
      .pipe(
        map((response) => {
          return response;
        })
      );
  }

  getFieldActivityCheckResultsByDate(startDate, endDate, year): Observable<ChecksResultByDate[]> {
    return this.http
      .get<any>(
        `${this.checksServiceUrl}api/private/v1/fa-check-result/date/?cropYear=${year}&startDate=${startDate}&endDate=${endDate}`
      )
      .pipe(
        map((response) => {
          return response;
        })
      );
  }

  //! When calling this method, make sure getFACheckResults has been called and has returned a value
  getFieldActivityCheckResultsByFAid(id: number) {
    const result = this.$faChecksResults.getValue();

    const faChecksResult = result.filter(
      (res) => res.fieldActivityId == id && res.checkResult == 'not_ok' && !res.ignore
    );

    const typeMap: Map<string, Set<string>> = new Map();

    faChecksResult.forEach((faRes) => {
      if (!typeMap.has(faRes.checkRuleType)) {
        typeMap.set(faRes.checkRuleType, new Set());
      }
      typeMap.get(faRes.checkRuleType).add(faRes.checkRuleCategory);
    });

    return Array.from(typeMap.entries()).map(([type, categories]) => ({
      type,
      category: Array.from(categories),
    }));
  }

  getSubstancesIntakeAllFields(year, elements): Observable<any> {
    const elQueryParam = [];
    elements.forEach((param) => elQueryParam.push(`&elementIds=${param}`));
    const elQueryParamString = elQueryParam.join(',').replace(/,/g, '');
    return this.http
      .get<any>(
        `${this.checksServiceUrl}api/private/v1/fa-check-result/npk/?cropYear=${year}${elQueryParamString}`
      )
      .pipe(
        map((response) => {
          return response;
        })
      );
  }

  getSubstancesIntakeByField(year, fieldId, elements): Observable<any> {
    const elQueryParam = [];
    elements.forEach((param) => elQueryParam.push(`&elementIds=${param}`));
    const elQueryParamString = elQueryParam.join(',').replace(/,/g, '');
    return this.http
      .get<any>(
        `${this.checksServiceUrl}api/private/v1/fa-check-result/npk/?fieldId=${fieldId}&cropYear=${year}${elQueryParamString}`
      )
      .pipe(
        map((response) => {
          return response;
        })
      );
  }

  getCorrelatedActivitiesForFA(id): Observable<any> {
    return this.http
      .get<any>(
        `${this.checksServiceUrl}api/private/v1/fa-check-result/correlated/?fieldActivity=${id}`
      )
      .pipe(
        map((response) => {
          return response;
        })
      );
  }

  ignoreFACheckResult(checksResult) {
    return this.http
      .put(`${this.checksServiceUrl}api/private/v1/fa-check-result/`, checksResult)
      .pipe(
        map((response) => {
          return response;
        })
      );
  }

  getCropCheckConfig(): Observable<any> {
    return this.http.get<any>(`${this.checksServiceUrl}api/private/v1/crop-check-config`).pipe(
      map((response) => {
        return response;
      })
    );
  }

  saveCropCheckConfig(config, method) {
    if (method === 'PUT') {
      return this.http.put(`${this.checksServiceUrl}api/private/v1/crop-check-config`, config).pipe(
        map((response) => {
          return response;
        })
      );
    } else if (method === 'POST') {
      return this.http
        .post(`${this.checksServiceUrl}api/private/v1/crop-check-config`, config)
        .pipe(
          map((response) => {
            return response;
          })
        );
    }
    // Ensure a value is always returned
    return null;
  }

  saveCropCheckMultiConfig(config): Observable<any> {
    return this.http.put(`${this.apiBaseUrl}api/private/v1/crops/checks-config/crops`, config).pipe(
      map((response) => {
        return response;
      })
    );
  }

  saveFieldCheckMultiConfig(config): Observable<any> {
    return this.http
      .put(`${this.apiBaseUrl}api/private/v1/crops/checks-config/fields`, config)
      .pipe(
        map((response) => {
          return response;
        })
      );
  }

  checkRegionIsSelected(fightType, region) {
    if (fightType && fightType.length && fightType == 'integrated_disciplinary') {
      if (region && region.length) {
        return true;
      } else {
        return false;
      }
    } else {
      return true;
    }
  }

  getFightTypesForDropdown() {
    return [
      {
        id: '1',
        itemName: this.translate.instant('checks.fight_type.integrated_disciplinary'),
        itemKey: 'integrated_disciplinary',
      },
      {
        id: '2',
        itemName: this.translate.instant('checks.fight_type.integrated'),
        itemKey: 'integrated',
      },
      {
        id: '3',
        itemName: this.translate.instant('checks.fight_type.organic'),
        itemKey: 'organic',
      },
    ];
  }

  getChartColorsByType(Type) {
    if (checksTypeColor.hasOwnProperty(Type)) {
      return checksTypeColor[Type];
    }
    return '#FFFFFF';
  }

  getChartColorsByPhytoCategory(category) {
    if (checksPhytoCategoryColor.hasOwnProperty(category)) {
      return checksPhytoCategoryColor[category];
    }
    return '#FFFFFF';
  }

  getAllPossibleParamsForSummaryActivity(): string[] {
    return [
      'activityDate',
      'adversity',
      'doseUsed',
      'unitUsed',
      'activeSubstance',
      'harvestDate',
      'daysElapsed',
      'numTratt',
      // 'error',
      // 'productName',
      // 'productId',
      // 'unit',
      // 'doseMin',
      // 'doseMax',
      // 'unitDisciplinary',
      // 'carencyDay',
      // 'numMaxTratt',
      // 'daysThreshold',
      // 'date',
      // 'lastUseDate',
      // 'preHarvestingDay',
      // 'region',
      // 'season',
      // 'moleculesGroup',
      // 'cropYear',
      // 'doseLimit',
      // 'concentration',
      // 'concentrationDisciplinary',
      // 'concentrationUsed'
    ];
  }

  getBgColorChipsByType(type: string): string {
    switch (type) {
      case 'numDefending':
      case 'defending':
        return '#77AD90';
      case 'numFeeding':
      case 'feeding':
        return '#813A4C';
      case 'numContainer':
      case 'container':
        return '#CEDB8C';
      case 'numIrrigating':
      case 'irrigating':
        return '#608CC9';
      case 'numGeneric':
      case 'generic':
        return '#46155C';
      case 'license':
        return '#E88D2B';
      default:
        return '#FFF';
    }
  }

  getBorderColorChipsByCategory(category: string): string {
    switch (category) {
      // defending
      case 'dose':
      case 'active_substance':
      case 'max_treatment':
      case 'treatment_break':
      case 'product_revocated':
      case 'barrel_water':
      case 'preharvesting':
      case 'over_last_use':
      case 'dose_threshold_cu':
      case 'max_treatment_disciplinary':
      case 'active_substance_crop_disciplinary':
      case 'dose_threshold_disciplinary':
      case 'over_last_use_disciplinary':
      case 'max_treatment_substance':
      case 'max_treatment_molecule_disciplinary':
      case 'active_substance_concentration_disciplinary':
      case 'bio_product':
      case 'max_treatment_substance_disciplinary':
        return '#77AD90';
      // feeding
      case 'dose_unit_n':
      case 'dose_unit_p':
      case 'dose_unit_k':
      case 'dose_unit_n_zoo':
      case 'dose_exceeded_zdn':
        return '#813A4C';
      // container
      case 'storage':
        return '#CEDB8C';
      // irrigating
      case 'no_irrigation':
        return '#608CC9';
      // generic
      case 'adversity':
      case 'dose_unit':
      case 'product':
      case 'mix_not_present':
      case 'license':
        return '#46155C';
      default:
        return '#FFF';
    }
  }
}
